import { useEffect, useState } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';

const API_URL = process.env.REACT_APP_BACKEND_URL || '';

/**
 * SSOHandler
 * Mount anywhere near the root. Detects ?sso=xxx in the URL and exchanges
 * it for a full JWT, storing it in localStorage. Then clears the query
 * param and redirects to the intended destination (from ?to=... or '/').
 *
 * Usage: <SSOHandler /> at top of App.
 */
export default function SSOHandler() {
 const [params, setParams] = useSearchParams();
 const navigate = useNavigate();
 const [status, setStatus] = useState('idle'); // idle | exchanging | done | error

 useEffect(() => {
 const sso = params.get('sso');
 if (!sso || status !== 'idle') return;

 setStatus('exchanging');
 (async () => {
 try {
 const r = await fetch(`${API_URL}/api/auth/sso/consume`, {
 method: 'POST',
 headers: { 'Content-Type': 'application/json' },
 body: JSON.stringify({ sso_token: sso }),
 });
 if (!r.ok) throw new Error('SSO failed');
 const data = await r.json();
 localStorage.setItem('token', data.access_token);
 localStorage.setItem('user', JSON.stringify(data.user));
 const to = params.get('to') || '/';
 // Strip SSO params from URL
 params.delete('sso');
 params.delete('to');
 setParams(params, { replace: true });
 setStatus('done');
 navigate(to, { replace: true });
 } catch (e) {
 console.error('SSO exchange failed:', e);
 setStatus('error');
 }
 })();
 }, [params, status, setParams, navigate]);

 if (status === 'exchanging') {
 return (
 <div className="fixed inset-0 z-[9999] flex items-center justify-center bg-white/95 backdrop-blur-sm">
 <div className="text-center">
 <div className="w-12 h-12 mx-auto border-4 border-[#D85308]/30 border-t-[#D85308] rounded-full animate-spin" />
 <p className="mt-4 text-sm font-bold text-slate-700">Signing you in…</p>
 </div>
 </div>
 );
 }
 return null;
}

/**
 * Helper — generate an SSO redirect URL.
 * Call before window.location redirects to another subdomain.
 *
 * @param {string} targetUrl - e.g. 'https://platform.example.com'
 * @param {string} [toPath] - path inside target, e.g. '/dashboard'
 * @returns {Promise<string>} - full URL with ?sso=xxx&to=/path
 */
export async function buildSSORedirectUrl(targetUrl, toPath = '/') {
 const token = localStorage.getItem('token');
 if (!token) return targetUrl + toPath;
 try {
 const r = await fetch(`${API_URL}/api/auth/sso/exchange`, {
 method: 'POST',
 headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
 body: JSON.stringify({ target: 'platform' }),
 });
 if (!r.ok) return targetUrl + toPath;
 const { sso_token } = await r.json();
 const url = new URL(toPath, targetUrl);
 url.searchParams.set('sso', sso_token);
 url.searchParams.set('to', toPath);
 return url.toString();
 } catch {
 return targetUrl + toPath;
 }
}
